'use client';
import { useFormatter } from 'next-intl';
import { memo } from 'react';
import { UserAvatar } from '@/app/components/shared/UserAvatar';
import { SITE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { formatDateTime } from '@/app/utils';
import type { ChatMessageItem, ClawContent } from '../../../types';
import { MessageAttachmentList } from './MessageAttachmentList';

const SHARE_AVATAR_URL_EN = 'https://image01.cf.vidu.studio/vidu/media-asset/author-img-02bbb0f4.webp';
const SHARE_AVATAR_URL_ZH = 'https://image01-aliyun.vidu.zone/vidu/media-asset/author-img-02bbb0f4.webp';
const SHARE_AVATAR_URL = SITE === 'china' ? SHARE_AVATAR_URL_ZH : SHARE_AVATAR_URL_EN;

interface UserMessageProps {
  message: ChatMessageItem;
  fileList?: ClawContent[];
  clawId?: string;
  type?: 'shareSession';
  onPreviewFile?: (filePath: string) => void;
  onQuoteFile?: (filePath: string) => void;
}

export const UserMessage = memo(({ message, clawId, type, onPreviewFile, onQuoteFile }: UserMessageProps) => {
  const format = useFormatter();
  const userInfo = useStore((state) => state.userInfo);

  const { content, fileList = [], createdAt = '' } = message;

  return (
    <div className="flex items-start gap-3 pt-4" data-anchor-id={message.id} data-anchor-role="user">
      <div className="flex min-w-0 flex-1 flex-col gap-[6px]">
        <div className="flex justify-end">
          <div className="flex w-fit max-w-[90%] flex-col justify-end rounded-tl-16 rounded-tr-8 rounded-br-16 rounded-bl-8 bg-[rgba(87,101,242,0.16)] px-4 py-3 text-sm">
            {fileList && fileList.length > 0 ? (
              <div className="mb-2">
                <MessageAttachmentList
                  files={fileList}
                  clawId={clawId}
                  align="right"
                  onPreviewFile={onPreviewFile}
                  onQuoteFile={onQuoteFile}
                />
              </div>
            ) : null}
            <div className="whitespace-pre-line">{content}</div>
          </div>
        </div>
        <div className="mt-1 text-right text-system-white36 text-xs">{formatDateTime(createdAt, format)}</div>
      </div>
      {type === 'shareSession' ? (
        <img
          src={SHARE_AVATAR_URL}
          alt="share avatar"
          className="size-8 shrink-0 overflow-hidden rounded-full sm:hidden"
        />
      ) : (
        userInfo && <UserAvatar userInfo={userInfo} className="size-8 shrink-0 overflow-hidden rounded-full" />
      )}
    </div>
  );
});

UserMessage.displayName = 'UserMessage';
